import { ReactNode } from "react";

export interface BlogAuthor {
  name: string;
  role: string;
  url?: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  dateISO: string;
  keywords: string;
  author?: BlogAuthor;
  content: ReactNode;
}

export const defaultAuthor: BlogAuthor = {
  name: "Piktable",
  role: "Équipe Piktable, Lausanne",
};

export const blogPosts: BlogPost[] = [
  {
    slug: "no-show-restaurant-solutions",
    title: "No-show au restaurant : comprendre et réduire les réservations non honorées",
    excerpt: "Les no-shows coûtent cher aux restaurants. Découvrez les causes principales et les solutions concrètes pour réduire les réservations non honorées dans votre établissement.",
    category: "Guide",
    date: "07.04.2026",
    dateISO: "2026-04-07",
    keywords: "no-show restaurant, reservation non honoree, reduire no-show, restaurant suisse",
    author: defaultAuthor,
    content: (
      <>
        <p>
          Un client réserve une table pour quatre personnes un vendredi soir. L'heure passe, personne ne se présente,
          et aucun appel pour annuler. La table reste vide pendant que d'autres clients ont été refusés faute de place.
          Ce scénario, tout restaurateur en Suisse romande le connaît. C'est ce qu'on appelle un <strong>no-show</strong>.
        </p>
        <p>
          Au-delà de la frustration, les no-shows ont un impact financier direct sur votre établissement. Comprendre
          leurs causes et mettre en place des solutions adaptées peut faire une vraie différence sur votre chiffre d'affaires.
        </p>

        <h2>Qu'est-ce qu'un no-show et pourquoi c'est un problème</h2>
        <p>
          Un no-show désigne un client qui a réservé une table mais ne se présente pas, sans prévenir ni annuler.
          Ce n'est pas une simple impolitesse : c'est une perte sèche pour le restaurant.
        </p>
        <p>
          Une table vide un vendredi soir, c'est un manque à gagner immédiat. Les ingrédients ont été commandés,
          le personnel est en place, et d'autres clients ont potentiellement été refusés. Pour un petit établissement
          avec 30 à 40 couverts, même deux ou trois no-shows par service peuvent représenter une perte significative
          sur le mois.
        </p>
        <p>
          Le problème est d'autant plus marqué lors des périodes de forte demande : fêtes de fin d'année,
          Saint-Valentin, week-ends d'été en terrasse. Ce sont précisément les moments où chaque table compte le plus.
        </p>

        <h2>Les causes principales des no-shows</h2>
        <p>
          Avant de chercher des solutions, il est utile de comprendre pourquoi les clients ne viennent pas.
          Dans la grande majorité des cas, il ne s'agit pas de mauvaise volonté.
        </p>

        <h3>L'oubli pur et simple</h3>
        <p>
          C'est la cause la plus fréquente. Le client réserve plusieurs jours à l'avance, puis oublie.
          Sans rappel, la réservation tombe dans l'oubli au milieu d'un emploi du temps chargé.
        </p>

        <h3>La double réservation</h3>
        <p>
          Certains clients réservent dans deux ou trois restaurants différents pour « se laisser le choix »,
          puis ne prennent pas la peine d'annuler les autres. Ce comportement est particulièrement courant
          quand la réservation ne coûte rien et ne demande aucun engagement.
        </p>

        <h3>Les imprévus de dernière minute</h3>
        <p>
          Maladie, problème familial, changement de plans : les imprévus existent. Le problème n'est pas
          l'imprévu en soi, mais l'absence de communication. Beaucoup de clients n'osent pas appeler
          pour annuler, surtout à la dernière minute.
        </p>

        <h3>La facilité de réservation sans engagement</h3>
        <p>
          Plus il est facile de réserver sans conséquence, plus le risque de no-show augmente. Un simple
          appel téléphonique crée déjà un lien humain qui rend l'annulation plus naturelle. Un formulaire
          en ligne sans confirmation, beaucoup moins.
        </p>

        <h2>Solutions concrètes pour réduire les no-shows</h2>
        <p>
          Il n'existe pas de solution miracle, mais une combinaison de bonnes pratiques permet de réduire
          considérablement le taux de no-show.
        </p>

        <h3>Les rappels automatiques</h3>
        <p>
          C'est la mesure la plus efficace et la moins intrusive. Un simple email ou SMS envoyé 24 heures
          avant la réservation rappelle au client son engagement et lui donne l'occasion d'annuler facilement
          s'il ne peut plus venir.
        </p>
        <p>
          L'idéal est d'inclure un lien d'annulation directement dans le rappel. Cela réduit la friction :
          le client n'a pas besoin d'appeler, il peut libérer la table en un clic. C'est mieux qu'une table
          bloquée inutilement.
        </p>

        <h3>La confirmation requise</h3>
        <p>
          Demander au client de confirmer sa réservation (par email ou SMS) ajoute un engagement supplémentaire.
          Un client qui a activement confirmé sa venue est beaucoup moins susceptible de ne pas se présenter.
        </p>

        <h3>L'empreinte bancaire</h3>
        <p>
          Pour les établissements haut de gamme ou lors d'événements spéciaux (Saint-Sylvestre, menus spéciaux),
          demander une empreinte bancaire au moment de la réservation est une pratique de plus en plus courante.
          Elle ne facture rien au client, mais permet de prélever un montant en cas de no-show non excusé.
        </p>
        <p>
          Cette approche est efficace mais doit être utilisée avec discernement. Pour un bistrot de quartier,
          cela peut sembler excessif. Pour un restaurant gastronomique avec un menu à 120 CHF, c'est parfaitement
          justifié.
        </p>

        <h3>Une politique d'annulation claire</h3>
        <p>
          Communiquer clairement votre politique d'annulation au moment de la réservation est essentiel.
          Indiquez le délai d'annulation (par exemple, 4 heures avant) et les éventuelles conséquences
          d'un no-show. La transparence crée un cadre respectueux pour les deux parties.
        </p>

        <h3>La liste d'attente</h3>
        <p>
          Maintenir une liste d'attente permet de combler rapidement une annulation de dernière minute.
          Si un client annule à 17h pour un service de 19h, vous pouvez immédiatement proposer la table
          au premier client en attente.
        </p>

        <h2>Comment un logiciel de réservation aide concrètement</h2>
        <p>
          Gérer les rappels, confirmations et listes d'attente manuellement est chronophage. C'est précisément
          là qu'un logiciel de réservation fait la différence.
        </p>
        <ul>
          <li><strong>Rappels automatiques</strong> : envoyés sans intervention de votre part, au bon moment</li>
          <li><strong>Confirmation en un clic</strong> : le client confirme ou annule directement depuis l'email</li>
          <li><strong>Historique client</strong> : identifiez les clients récidivistes et adaptez votre politique</li>
          <li><strong>Gestion de la liste d'attente</strong> : remplissez les tables libérées automatiquement</li>
          <li><strong>Statistiques</strong> : suivez votre taux de no-show et mesurez l'impact de vos actions</li>
        </ul>
        <p>
          Piktable envoie des rappels automatiques par email à vos clients avant chaque réservation. Le client
          peut confirmer ou annuler en un clic, ce qui vous permet de réagir rapidement et de réattribuer la table.
        </p>

        <h2>En résumé</h2>
        <p>
          Les no-shows font partie de la réalité de la restauration, mais ils ne sont pas une fatalité.
          En combinant rappels automatiques, politique d'annulation claire et outils adaptés, vous pouvez
          réduire significativement leur impact sur votre établissement. L'objectif n'est pas de punir
          les clients, mais de créer un système où annuler est aussi simple que réserver.
        </p>
      </>
    ),
  },
  {
    slug: "reservation-en-ligne-restaurant-avantages",
    title: "Réservation en ligne pour restaurant : pourquoi abandonner le carnet papier",
    excerpt: "Le carnet de réservation papier a ses limites. Découvrez pourquoi passer à la réservation en ligne simplifie la gestion de votre restaurant au quotidien.",
    category: "Guide",
    date: "07.04.2026",
    dateISO: "2026-04-07",
    keywords: "reservation en ligne restaurant, logiciel reservation restaurant, digitalisation restaurant",
    author: defaultAuthor,
    content: (
      <>
        <p>
          Le carnet de réservation a longtemps été l'outil de référence dans la restauration. Simple,
          tangible, il ne nécessite ni connexion internet ni formation. Pourtant, à mesure que les
          habitudes des clients évoluent, ses limites deviennent de plus en plus évidentes.
        </p>
        <p>
          Aujourd'hui, la majorité des clients s'attendent à pouvoir réserver en ligne, à toute heure,
          depuis leur téléphone. Passer à la réservation en ligne n'est pas une question de mode, mais
          d'efficacité et de service client.
        </p>

        <h2>Les limites du carnet papier</h2>
        <p>
          Le carnet papier fonctionne, mais il crée des frictions invisibles qui coûtent du temps et
          potentiellement des clients.
        </p>

        <h3>Les erreurs de transcription</h3>
        <p>
          Un nom mal noté, un chiffre illisible, une date confondue avec une autre : les erreurs humaines
          sont inévitables quand tout passe par l'écriture manuscrite. En plein coup de feu, noter une
          réservation téléphonique tout en servant des clients est un exercice d'équilibriste.
        </p>

        <h3>Les doubles réservations</h3>
        <p>
          Sans vue d'ensemble en temps réel, il arrive qu'une même table soit attribuée à deux clients
          différents. Le problème ne se révèle qu'au moment du service, quand il est trop tard pour
          réagir sereinement.
        </p>

        <h3>Aucune visibilité à distance</h3>
        <p>
          Avec un carnet papier, seule la personne physiquement présente au restaurant peut consulter
          les réservations. Si vous avez plusieurs collaborateurs, ou si vous souhaitez vérifier le
          planning depuis chez vous, c'est impossible.
        </p>

        <h3>Les réservations perdues en dehors des heures d'ouverture</h3>
        <p>
          Un client qui veut réserver à 22h un mardi soir ne va pas attendre le lendemain matin pour
          appeler. Il ira sur un autre restaurant qui accepte les réservations en ligne. Chaque appel
          manqué est potentiellement un client perdu.
        </p>

        <h2>Les avantages concrets de la réservation en ligne</h2>
        <p>
          Passer au digital ne signifie pas abandonner le contact humain. Cela signifie automatiser
          ce qui peut l'être pour vous concentrer sur ce qui compte : l'accueil et la cuisine.
        </p>

        <h3>Disponible 24 heures sur 24</h3>
        <p>
          Vos clients peuvent réserver à tout moment : le soir depuis leur canapé, le matin dans les
          transports, le dimanche quand votre restaurant est fermé. Vous ne perdez plus de réservations
          parce que personne n'était là pour décrocher le téléphone.
        </p>

        <h3>Moins d'erreurs, moins de stress</h3>
        <p>
          Le client saisit lui-même son nom, le nombre de personnes, la date et l'heure. Plus de risque
          de malentendu téléphonique ou de griffonnage illisible. Les informations sont structurées et
          consultables en un coup d'œil.
        </p>

        <h3>Des données clients exploitables</h3>
        <p>
          Avec un système de réservation en ligne, vous constituez automatiquement une base de données
          clients. Nombre de visites, préférences, allergies mentionnées, taille de groupe habituelle :
          ces informations vous permettent d'offrir un service plus personnalisé.
        </p>

        <h3>Un gain de temps quotidien</h3>
        <p>
          Moins de temps passé au téléphone, moins de recopiage, moins de vérifications manuelles.
          Votre équipe peut se concentrer sur le service en salle plutôt que sur la gestion administrative
          des réservations.
        </p>

        <h2>Ce que ça change pour un petit restaurant</h2>
        <p>
          On pourrait penser que la réservation en ligne est réservée aux grands établissements. C'est
          l'inverse : c'est justement les petites structures qui en bénéficient le plus.
        </p>
        <p>
          Un restaurant de 25 couverts n'a pas de réceptionniste dédié. C'est souvent le patron ou un
          serveur qui décroche le téléphone entre deux services. Chaque interruption coûte du temps et
          de l'énergie. Avec la réservation en ligne, ces interruptions disparaissent.
        </p>
        <p>
          De plus, un petit restaurant ne peut pas se permettre de perdre des réservations. Chaque table
          compte. Être accessible 24h/24 pour les réservations, c'est maximiser son taux de remplissage
          sans effort supplémentaire.
        </p>

        <h2>Comment choisir son outil de réservation</h2>
        <p>
          Tous les outils de réservation ne se valent pas. Voici les critères essentiels à considérer
          pour un restaurant en Suisse romande.
        </p>
        <ul>
          <li>
            <strong>Simplicité d'utilisation</strong> : l'outil doit être intuitif, autant pour vous que pour
            vos clients. Si la prise en main nécessite une formation de deux heures, c'est trop compliqué.
          </li>
          <li>
            <strong>Pas de commission par couvert</strong> : certaines plateformes prélèvent 2 à 3 CHF par
            couvert réservé. Sur un mois complet, la facture peut devenir salée. Privilégiez un modèle à coût
            fixe et prévisible.
          </li>
          <li>
            <strong>Support local</strong> : un outil basé en Suisse romande comprend vos réalités (horaires,
            saisonnalité, attentes des clients locaux) et offre un support dans votre langue.
          </li>
          <li>
            <strong>Intégration avec votre site web</strong> : vos clients doivent pouvoir réserver directement
            depuis votre site, sans être redirigés vers une plateforme tierce.
          </li>
          <li>
            <strong>Données vous appartenant</strong> : vos données clients doivent rester les vôtres, pas celles
            d'une marketplace qui les utilise pour promouvoir vos concurrents.
          </li>
        </ul>
        <p>
          Piktable propose un widget de réservation embeddable que vous pouvez intégrer directement sur le site
          de votre restaurant. Vos clients réservent chez vous, pas sur une plateforme tierce.
        </p>

        <h2>Faire la transition en douceur</h2>
        <p>
          Passer du carnet papier au digital ne doit pas se faire du jour au lendemain. Commencez par
          accepter les réservations en ligne en parallèle du téléphone. Vos clients réguliers garderont
          leurs habitudes, tandis que les nouveaux clients apprécieront la possibilité de réserver en ligne.
        </p>
        <p>
          Avec le temps, vous constaterez que la majorité des réservations arrivent en ligne, et que le
          téléphone sonne de moins en moins pour des réservations. C'est le signe que la transition
          fonctionne.
        </p>
      </>
    ),
  },
  {
    slug: "commission-par-couvert-cout-restaurant",
    title: "Commission par couvert : ce que les marketplaces coûtent vraiment à votre restaurant",
    excerpt: "Les plateformes de réservation facturent souvent une commission par couvert. Analysons le coût réel et les alternatives pour votre restaurant.",
    category: "Analyse",
    date: "07.04.2026",
    dateISO: "2026-04-07",
    keywords: "commission par couvert, TheFork commission, reservation sans commission, marketplace restaurant",
    author: defaultAuthor,
    content: (
      <>
        <p>
          Les plateformes de réservation comme TheFork (anciennement LaFourchette) offrent une visibilité
          indéniable aux restaurants. En échange, elles prélèvent une commission sur chaque couvert réservé.
          Ce modèle peut sembler anodin au départ, mais les montants s'accumulent rapidement.
        </p>
        <p>
          Comprendre le coût réel de ces commissions est essentiel pour décider si elles représentent un
          investissement rentable ou une dépense évitable.
        </p>

        <h2>Comment fonctionne le modèle commission par couvert</h2>
        <p>
          Le principe est simple : chaque fois qu'un client réserve via la plateforme et se présente au
          restaurant, une commission est facturée au restaurateur. Le montant varie selon la plateforme
          et le plan choisi, mais se situe généralement entre 2 et 3 CHF par couvert.
        </p>
        <p>
          Ce modèle est attractif à première vue : vous ne payez que quand un client vient effectivement
          manger. Pas de frais fixes, pas d'abonnement. Mais c'est précisément cette apparente simplicité
          qui masque le coût réel.
        </p>

        <h2>Le calcul concret : combien ça coûte vraiment</h2>
        <p>
          Prenons un exemple réaliste pour un restaurant de taille moyenne en Suisse romande.
        </p>

        <h3>Scénario : 200 couverts par mois via la plateforme</h3>
        <p>
          Un restaurant qui reçoit 200 réservations mensuelles via une marketplace, avec une commission
          de 2.50 CHF par couvert, paie <strong>500 CHF par mois</strong>, soit <strong>6 000 CHF par an</strong>.
        </p>
        <p>
          Et ce chiffre ne prend en compte que les couverts réservés via la plateforme. Si votre restaurant
          gagne en popularité et que le volume augmente, la facture suit proportionnellement. À 400 couverts
          par mois, c'est 1 000 CHF mensuels, soit 12 000 CHF par an.
        </p>

        <h3>Le piège de la croissance</h3>
        <p>
          Contrairement à un abonnement fixe, le modèle à la commission pénalise votre succès. Plus vous
          avez de réservations, plus vous payez. C'est exactement l'inverse de ce qu'un restaurateur
          souhaite : que ses coûts fixes diminuent proportionnellement à mesure que son activité croît.
        </p>

        <h2>Ce que vous perdez au-delà de l'argent</h2>
        <p>
          La commission financière n'est que la partie visible. Les marketplaces créent d'autres coûts,
          moins évidents mais tout aussi importants.
        </p>

        <h3>La relation client vous échappe</h3>
        <p>
          Quand un client réserve via une marketplace, c'est la plateforme qui possède la relation.
          Le client reçoit des emails de la marketplace, pas de votre restaurant. Si vous quittez la
          plateforme, vous perdez l'accès à ces clients.
        </p>

        <h3>Vos concurrents sont à un clic</h3>
        <p>
          Sur une marketplace, votre restaurant est affiché à côté de vos concurrents directs. Un client
          qui cherche votre établissement peut être tenté par une promotion ou une meilleure note chez
          le voisin. Vous financez une plateforme qui facilite la comparaison avec vos concurrents.
        </p>

        <h3>La dépendance s'installe</h3>
        <p>
          Plus vous recevez de réservations via la plateforme, plus il devient difficile de s'en passer.
          C'est un cercle de dépendance : la marketplace devient votre principal canal d'acquisition,
          et vous perdez la capacité de fonctionner sans elle.
        </p>

        <h2>L'alternative : la réservation directe</h2>
        <p>
          La réservation directe consiste à proposer la réservation en ligne depuis votre propre site
          web ou vos réseaux sociaux, sans passer par une marketplace.
        </p>

        <h3>Un coût fixe et prévisible</h3>
        <p>
          Avec un logiciel de réservation en direct, vous payez un abonnement mensuel fixe, quel que
          soit le nombre de couverts. Que vous receviez 50 ou 500 réservations par mois, le coût reste
          le même. C'est budgétisable et sans surprise.
        </p>

        <h3>Vos données restent les vôtres</h3>
        <p>
          Chaque réservation enrichit votre propre base de données clients. Vous pouvez contacter vos
          clients directement, leur envoyer des offres personnalisées, les fidéliser. Ces données ont
          une valeur considérable à long terme.
        </p>

        <h3>Votre marque au premier plan</h3>
        <p>
          Le client réserve sur votre site, dans votre univers visuel. Il n'est pas distrait par d'autres
          restaurants ou des promotions concurrentes. L'expérience de réservation fait partie de
          l'expérience globale de votre restaurant.
        </p>

        <h2>Comparaison chiffrée</h2>
        <p>
          Mettons les deux modèles côte à côte pour un restaurant qui reçoit 200 couverts par mois
          via ses réservations en ligne.
        </p>
        <ul>
          <li>
            <strong>Marketplace (commission par couvert)</strong> : 200 × 2.50 CHF = 500 CHF/mois,
            soit 6 000 CHF/an. Le coût augmente avec le volume.
          </li>
          <li>
            <strong>Logiciel en direct (abonnement fixe)</strong> : coût mensuel plafonné, indépendant
            du volume. Piktable, par exemple, plafonne ses coûts à maximum 99 CHF/mois.
          </li>
        </ul>
        <p>
          La différence est claire : sur une année, un restaurant actif peut économiser plusieurs milliers
          de francs en passant à la réservation directe.
        </p>

        <h2>Les marketplaces ont-elles encore un rôle ?</h2>
        <p>
          Les marketplaces ne sont pas inutiles. Elles peuvent être pertinentes pour un nouveau restaurant
          qui cherche à se faire connaître rapidement, ou pour un établissement touristique qui vise une
          clientèle internationale.
        </p>
        <p>
          Mais pour un restaurant établi avec une clientèle locale fidèle, la réservation directe est
          presque toujours plus avantageuse. L'idéal est de combiner les deux au départ, puis de
          progressivement rediriger les clients vers votre propre système de réservation.
        </p>

        <h2>En résumé</h2>
        <p>
          La commission par couvert est un modèle qui profite aux plateformes, pas aux restaurateurs.
          En passant à un outil de réservation directe à coût fixe, vous reprenez le contrôle de
          votre relation client, de vos données et de vos coûts. C'est un investissement dans
          l'autonomie de votre établissement.
        </p>
      </>
    ),
  },
  {
    slug: "widget-reservation-restaurant-integration",
    title: "Comment intégrer un widget de réservation sur le site de votre restaurant",
    excerpt: "Un widget de réservation sur votre site permet à vos clients de réserver directement, sans commission. Voici comment ça fonctionne et ce qu'un bon widget doit offrir.",
    category: "Tutoriel",
    date: "07.04.2026",
    dateISO: "2026-04-07",
    keywords: "widget reservation restaurant, reservation site web restaurant, integrer reservation",
    author: defaultAuthor,
    content: (
      <>
        <p>
          Avoir un site web pour votre restaurant, c'est bien. Permettre à vos clients de réserver
          directement depuis ce site, c'est encore mieux. Un widget de réservation intégré à votre site
          transforme chaque visiteur en client potentiel, sans qu'il ait besoin de décrocher le téléphone
          ou de passer par une plateforme tierce.
        </p>

        <h2>Pourquoi intégrer un widget de réservation sur votre site</h2>
        <p>
          Votre site web est souvent le premier point de contact avec un nouveau client. Il consulte
          votre carte, regarde vos photos, lit vos horaires. Si à ce moment précis il peut aussi réserver
          en un clic, les chances qu'il le fasse sont bien plus élevées que s'il doit chercher votre
          numéro de téléphone ou naviguer vers une autre plateforme.
        </p>

        <h3>Autonomie et indépendance</h3>
        <p>
          Un widget sur votre propre site signifie que vous ne dépendez d'aucune marketplace. Pas de
          commission par couvert, pas de concurrent affiché à côté de vous, pas de conditions imposées
          par un tiers. Vous gardez le contrôle total.
        </p>

        <h3>Une expérience fluide pour le client</h3>
        <p>
          Le client reste sur votre site, dans votre univers. Il n'est pas redirigé vers une plateforme
          externe avec son propre design et ses propres promotions. L'expérience est cohérente du début
          à la fin.
        </p>

        <h3>Conversion immédiate</h3>
        <p>
          En marketing, chaque étape supplémentaire dans un parcours client réduit le taux de conversion.
          Si le client doit quitter votre site pour réserver ailleurs, une partie d'entre eux abandonnera
          en chemin. Un widget intégré élimine cette friction.
        </p>

        <h2>Les différents types d'intégration</h2>
        <p>
          Il existe plusieurs façons d'ajouter la réservation en ligne à votre site web. Chacune a ses
          avantages selon votre situation technique.
        </p>

        <h3>Le lien direct</h3>
        <p>
          La méthode la plus simple : un bouton « Réserver » sur votre site qui ouvre la page de
          réservation dans un nouvel onglet ou sur la même page. Aucune compétence technique requise.
          Vous ajoutez un lien, c'est tout.
        </p>
        <p>
          C'est la solution idéale si vous avez un site simple (une page, un site créé avec un outil
          comme Squarespace ou WordPress) et que vous ne souhaitez pas toucher au code.
        </p>

        <h3>L'iframe intégré</h3>
        <p>
          L'iframe permet d'afficher le formulaire de réservation directement dans une page de votre
          site. Le client voit le widget sans quitter votre site. C'est plus intégré visuellement qu'un
          simple lien, mais nécessite un minimum de manipulation technique (copier-coller un bout de code
          HTML dans votre page).
        </p>

        <h3>Le bouton flottant</h3>
        <p>
          Un bouton fixe en bas de l'écran, visible sur toutes les pages de votre site. Le client peut
          réserver à tout moment, quelle que soit la page qu'il consulte. C'est une option très efficace
          pour maximiser la visibilité du bouton de réservation.
        </p>

        <h2>Ce qu'un bon widget de réservation doit offrir</h2>
        <p>
          Tous les widgets ne se valent pas. Voici les caractéristiques essentielles à vérifier avant
          de choisir votre solution.
        </p>
        <ul>
          <li>
            <strong>Responsive (adapté mobile)</strong> : la majorité de vos clients consulteront votre
            site depuis leur téléphone. Le widget doit s'afficher parfaitement sur tous les écrans,
            sans zoom ni défilement horizontal.
          </li>
          <li>
            <strong>Rapide à charger</strong> : un widget qui met 5 secondes à s'afficher fera fuir vos
            clients. Il doit être léger et se charger instantanément.
          </li>
          <li>
            <strong>Disponibilité en temps réel</strong> : le widget doit afficher les créneaux réellement
            disponibles, mis à jour en temps réel. Rien de pire pour un client que de réserver un créneau
            qui s'avère déjà complet.
          </li>
          <li>
            <strong>Personnalisable</strong> : le widget doit pouvoir s'adapter aux couleurs et au style
            de votre site. Un widget générique avec le logo d'un tiers détonne dans un site soigné.
          </li>
          <li>
            <strong>Simple d'utilisation</strong> : le client doit pouvoir réserver en moins d'une minute.
            Date, heure, nombre de personnes, nom, email : pas plus de champs que nécessaire.
          </li>
        </ul>

        <h2>Comment Piktable gère l'intégration</h2>
        <p>
          Piktable propose une approche simple et flexible pour l'intégration. Chaque restaurant dispose
          d'un lien de réservation unique au format <code>piktable.ch/votre-restaurant</code>, accessible
          depuis n'importe quel navigateur et optimisé pour mobile.
        </p>
        <p>
          Ce lien peut être utilisé de plusieurs façons :
        </p>
        <ul>
          <li>
            <strong>Bouton sur votre site</strong> : ajoutez simplement un bouton « Réserver » qui pointe
            vers votre lien Piktable.
          </li>
          <li>
            <strong>Lien dans votre bio Instagram</strong> : vos abonnés peuvent réserver directement
            depuis votre profil.
          </li>
          <li>
            <strong>QR code en salle</strong> : imprimez un QR code que vos clients scannent pour réserver
            leur prochaine visite avant de partir.
          </li>
          <li>
            <strong>Lien dans Google Maps</strong> : ajoutez votre lien de réservation à votre fiche Google
            Business pour que les clients réservent directement depuis la recherche Google.
          </li>
        </ul>
        <p>
          L'avantage de cette approche est sa simplicité : aucune compétence technique n'est requise.
          Vous copiez un lien, vous le collez où vous voulez, et c'est opérationnel.
        </p>

        <h2>Bonnes pratiques pour maximiser les réservations</h2>
        <p>
          Intégrer un widget ne suffit pas. Voici quelques conseils pour en tirer le meilleur parti.
        </p>
        <ul>
          <li>
            <strong>Rendez le bouton visible</strong> : placez-le en haut de page ou en bouton flottant.
            Si le client doit chercher comment réserver, c'est déjà trop tard.
          </li>
          <li>
            <strong>Utilisez un appel à l'action clair</strong> : « Réserver une table » est plus efficace
            que « Cliquez ici » ou « Contact ».
          </li>
          <li>
            <strong>Ajoutez le lien partout</strong> : site web, Google Maps, Instagram, Facebook, signature
            d'email. Chaque point de contact est une opportunité de réservation.
          </li>
          <li>
            <strong>Testez sur mobile</strong> : vérifiez régulièrement que le parcours de réservation
            fonctionne parfaitement sur téléphone. C'est là que la majorité de vos réservations se feront.
          </li>
        </ul>

        <h2>En résumé</h2>
        <p>
          Un widget de réservation sur votre site web est l'un des investissements les plus simples et
          les plus rentables pour votre restaurant. Il élimine les frictions, réduit votre dépendance
          aux plateformes tierces et offre à vos clients une expérience de réservation fluide. Et
          l'intégration ne prend que quelques minutes.
        </p>
      </>
    ),
  },
  {
    slug: "gestion-tables-restaurant-plan-salle",
    title: "Gestion de tables au restaurant : optimiser votre plan de salle",
    excerpt: "Un plan de salle bien organisé améliore le remplissage et le service. Découvrez comment optimiser la gestion de vos tables et de vos zones.",
    category: "Conseils",
    date: "07.04.2026",
    dateISO: "2026-04-07",
    keywords: "gestion tables restaurant, plan de salle restaurant, optimiser remplissage restaurant",
    author: defaultAuthor,
    content: (
      <>
        <p>
          La gestion des tables est au cœur du fonctionnement d'un restaurant. Pourtant, beaucoup
          d'établissements fonctionnent encore « de tête » ou avec un plan griffonné sur une feuille.
          Quand le service est calme, ça passe. Quand c'est complet un samedi soir, les erreurs
          d'attribution et les oublis peuvent vite gâcher la soirée.
        </p>
        <p>
          Un plan de salle structuré, qu'il soit digital ou non, est un outil de gestion essentiel.
          Il permet d'optimiser le remplissage, de réduire les temps d'attente et d'offrir un meilleur
          service à vos clients.
        </p>

        <h2>Plan de salle digital vs. méthode traditionnelle</h2>
        <p>
          Beaucoup de restaurateurs connaissent leur salle par cœur. Ils savent instinctivement quelle
          table convient à un couple, laquelle peut accueillir un groupe de six, et où placer les clients
          réguliers. Cette connaissance est précieuse, mais elle pose un problème : elle n'est pas
          transférable.
        </p>

        <h3>Les limites du plan mental</h3>
        <p>
          Quand le patron n'est pas là, qui gère les attributions ? Un nouveau serveur saura-t-il que
          la table 7 peut être combinée avec la table 8 pour les grands groupes ? Ou que la table 12
          près de la fenêtre est toujours réservée pour M. Müller le vendredi ?
        </p>
        <p>
          Le plan mental fonctionne tant que la même personne est toujours présente. Dès qu'il y a un
          remplacement, des vacances ou un deuxième établissement, les informations se perdent.
        </p>

        <h3>Les avantages d'un plan digital</h3>
        <p>
          Un plan de salle digital centralise toutes les informations en un seul endroit, accessible
          à toute l'équipe. Il montre en temps réel quelles tables sont libres, occupées ou réservées.
          Il permet de planifier le service à l'avance et de réagir rapidement aux imprévus.
        </p>
        <ul>
          <li><strong>Visibilité immédiate</strong> : en un coup d'œil, vous voyez l'état de chaque table</li>
          <li><strong>Accessible à tous</strong> : chaque membre de l'équipe a la même information</li>
          <li><strong>Historique</strong> : vous pouvez analyser vos taux de remplissage sur la durée</li>
          <li><strong>Planification</strong> : attribuez les tables à l'avance pour les réservations du soir</li>
        </ul>

        <h2>Les zones à gérer dans un restaurant</h2>
        <p>
          Un restaurant n'est pas qu'une seule salle. La plupart des établissements ont plusieurs zones,
          chacune avec ses spécificités et sa capacité propre.
        </p>

        <h3>La salle principale</h3>
        <p>
          C'est le cœur du restaurant, disponible toute l'année. C'est généralement la zone avec le
          plus de couverts et la plus de flexibilité dans l'arrangement des tables.
        </p>

        <h3>La terrasse</h3>
        <p>
          Atout majeur en Suisse romande dès les beaux jours, la terrasse est aussi la zone la plus
          imprévisible. Une averse soudaine peut vous obliger à déplacer tous les clients à l'intérieur.
          Gérer la terrasse comme une zone séparée, activable ou désactivable selon la météo, est essentiel.
        </p>

        <h3>Le bar ou le comptoir</h3>
        <p>
          Certains établissements proposent des places au bar pour les clients seuls ou les groupes
          informels. Ces places ont souvent un fonctionnement différent (sans réservation, service
          rapide) et doivent être gérées séparément.
        </p>

        <h3>Le salon privé</h3>
        <p>
          Pour les événements, les repas d'affaires ou les grandes tablées, un salon privé est un
          atout commercial important. Sa gestion est spécifique : réservation longue durée, menu
          spécial, configuration flexible.
        </p>

        <h2>Optimiser le remplissage de votre salle</h2>
        <p>
          Un bon plan de salle ne se contente pas de montrer où sont les tables. Il vous aide à
          maximiser le nombre de couverts servis par service.
        </p>

        <h3>Tables combinables</h3>
        <p>
          Avoir des tables de 2 qui peuvent être combinées en tables de 4 ou 6 offre une flexibilité
          précieuse. Quand vous recevez une réservation pour 5 personnes, vous pouvez assembler des
          petites tables plutôt que de bloquer une grande table toute la soirée.
        </p>
        <p>
          Pour que cela fonctionne, votre plan de salle doit indiquer clairement quelles tables sont
          combinables et dans quelles configurations.
        </p>

        <h3>Gestion des créneaux horaires</h3>
        <p>
          Proposer deux services le soir (par exemple 18h30 et 20h30) permet de doubler la capacité
          d'accueil sur les soirées de forte demande. Mais cela nécessite une gestion précise des
          horaires pour éviter les chevauchements.
        </p>
        <p>
          Un système de réservation avec créneaux définis vous permet de maîtriser la rotation des
          tables et d'éviter les situations où un client du premier service est encore installé quand
          le second commence.
        </p>

        <h3>La rotation des tables</h3>
        <p>
          En restauration, la rotation désigne le nombre de fois qu'une table est occupée par différents
          clients au cours d'un service. Une bonne rotation dépend de plusieurs facteurs : la durée
          moyenne du repas, le type de cuisine et l'efficacité du service.
        </p>
        <p>
          Suivre votre taux de rotation vous aide à identifier les opportunités d'amélioration. Si une
          table de 4 n'est utilisée qu'une fois par soirée alors que vos tables de 2 tournent deux fois,
          il y a peut-être un ajustement à faire.
        </p>

        <h2>La gestion multi-zones selon la météo</h2>
        <p>
          En Suisse romande, la météo joue un rôle majeur dans la gestion de salle. Un restaurant avec
          terrasse doit pouvoir adapter sa capacité en quelques minutes.
        </p>
        <p>
          Avec un plan de salle digital, vous pouvez activer ou désactiver la terrasse d'un simple
          geste. Les réservations sont automatiquement redirigées vers les zones disponibles. Vos clients
          en ligne voient uniquement les créneaux réellement accessibles, ce qui évite les mauvaises
          surprises.
        </p>
        <p>
          En début de saison, quand la météo est incertaine, vous pouvez même proposer les deux options
          au client au moment de la réservation : intérieur ou terrasse, sous réserve de la météo.
        </p>

        <h2>Piktable et la gestion de salle</h2>
        <p>
          Piktable offre un plan de salle en drag &amp; drop qui vous permet de configurer visuellement
          vos tables et vos zones. Vous glissez les tables, définissez leur capacité, indiquez lesquelles
          sont combinables et créez vos zones (salle, terrasse, salon privé).
        </p>
        <p>
          Le plan est ensuite utilisé par le système de réservation pour attribuer automatiquement les
          tables en fonction de la taille du groupe et des disponibilités. Votre équipe voit en temps
          réel l'état de chaque table et peut ajuster les attributions si nécessaire.
        </p>

        <h2>En résumé</h2>
        <p>
          La gestion de tables ne se résume pas à savoir combien de chaises vous avez. C'est un levier
          d'optimisation qui impacte directement votre chiffre d'affaires et la qualité de votre service.
          Un plan de salle digital, même simple, vous donne la visibilité et la flexibilité nécessaires
          pour tirer le meilleur de chaque service.
        </p>
      </>
    ),
  },
];
